// src/views/incidencias/IncidenciasListado.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE } from "../../apiConfig";

// 📌 Exportar Excel
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// 📌 Exportar PDF
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function IncidenciasListado() {
  const [incidencias, setIncidencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ==============================
  // CARGAR INCIDENCIAS
  // ==============================
  const cargarIncidencias = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/incidencias`);
      setIncidencias(res.data || []);
    } catch (err) {
      console.error("ERROR GET INCIDENCIAS:", err);
      setError("No se pudieron cargar las incidencias.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarIncidencias();
  }, []);

  // ==============================
  // EXPORTAR EXCEL
  // ==============================
  const exportarExcel = () => {
    const data = incidencias.map((i) => ({
      Equipo: i.equipo,
      Serie: i.serie,
      Ubicación: `${i.oficina || ""} - ${i.municipio || ""}`,
      Estado: i.estadoNuevo,
      Riesgo: i.nivelRiesgo,
      Fecha: formatearFecha(i.fechaReporte),
      Reportado_por: i.reportadoPor,
    }));

    const hoja = XLSX.utils.json_to_sheet(data);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "Incidencias");

    const excelBuffer = XLSX.write(libro, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([excelBuffer], { type: "application/octet-stream" }),
      "reporte_incidencias.xlsx"
    );
  };

  // ==============================
  // EXPORTAR PDF
  // ==============================
  const exportarPDF = () => {
    const doc = new jsPDF("landscape");
    doc.setFontSize(16);
    doc.text("Reporte de Incidencias de Equipos", 14, 15);

    const filas = incidencias.map((i) => [
      i.equipo || "-",
      i.serie || "-",
      `${i.oficina || ""} - ${i.municipio || ""}`,
      i.estadoNuevo || "-",
      i.nivelRiesgo || "-",
      formatearFecha(i.fechaReporte),
      i.reportadoPor || "-",
    ]);

    autoTable(doc, {
      startY: 25,
      head: [
        [
          "Equipo",
          "Serie",
          "Ubicación",
          "Estado",
          "Riesgo",
          "Fecha",
          "Reportado por",
        ],
      ],
      body: filas,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [30, 41, 59] },
    });

    doc.save("reporte_incidencias.pdf");
  };

  // ==============================
  // FORMATEAR FECHA
  // ==============================
  function formatearFecha(fecha) {
    if (!fecha) return "N/D";
    const d = new Date(fecha);
    return d.toLocaleString("es-MX", {
      year: "2-digit",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  // ==============================
  // RENDER
  // ==============================
  return (
    <div className="max-w-7xl mx-auto px-4">

      <h1 className="text-3xl font-bold text-slate-800">
        Incidencias de Equipos
      </h1>

      <p className="text-sm text-slate-500 mb-6">
        Panel de incidencias registradas.
      </p>

      <div className="flex gap-3 mb-6">
        <button
          onClick={exportarExcel}
          className="px-5 py-2 rounded-lg bg-green-600 text-white font-medium hover:bg-green-700"
        >
          Descargar Excel
        </button>

        <button
          onClick={exportarPDF}
          className="px-5 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700"
        >
          Descargar PDF
        </button>

        <button
          onClick={cargarIncidencias}
          className="px-5 py-2 rounded-lg bg-slate-900 text-white font-medium hover:bg-slate-700"
        >
          Actualizar
        </button>
      </div>

      {loading && (
        <p className="text-slate-500">Cargando incidencias...</p>
      )}

      {error && (
        <p className="text-red-600">{error}</p>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-900 text-white">
              <tr>
                <th className="px-4 py-3 text-left">Equipo</th>
                <th className="px-4 py-3 text-left">Serie</th>
                <th className="px-4 py-3 text-left">Ubicación</th>
                <th className="px-4 py-3 text-left">Estado</th>
                <th className="px-4 py-3 text-left">Riesgo</th>
                <th className="px-4 py-3 text-left">Fecha</th>
                <th className="px-4 py-3 text-left">Reportado por</th>
              </tr>
            </thead>

            <tbody>
              {incidencias.map((inc) => (
                <tr
                  key={inc.id}
                  className="border-b border-slate-100 hover:bg-slate-50"
                >
                  <td className="px-4 py-3">{inc.equipo || "-"}</td>
                  <td className="px-4 py-3">{inc.serie || "-"}</td>
                  <td className="px-4 py-3">
                    {inc.oficina || "-"} - {inc.municipio || "-"}
                  </td>
                  <td className="px-4 py-3">{inc.estadoNuevo || "-"}</td>
                  <td className="px-4 py-3">{inc.nivelRiesgo || "-"}</td>
                  <td className="px-4 py-3">
                    {formatearFecha(inc.fechaReporte)}
                  </td>
                  <td className="px-4 py-3">{inc.reportadoPor || "-"}</td>
                </tr>
              ))}
            </tbody>

          </table>
        </div>
      </div>
    </div>
  );
}
